// anchor 16 — DETAIL intention call_api 실패 시 LIST fallback 오도 차단 가이드.
//
// 문제 패턴 (L1-INFRA-002 / L13-API-ERROR-001):
//   사용자: "22번 락커 정보" → CHECK_LOCKER_DETAIL fail (Locker not found) →
//   LLM이 CHECK_LOCKER_LIST(50건) fallback 후 전체를 "22번 락커 관련 정보"로 finalize → user 오도.
//
// 본질 차단 (harness-over-regex, 3-layer):
//   Layer 1 — applyDetailNotFoundGuide: DETAIL fail tool_result에 1차 가이드 append.
//   Layer 2 — applyListFallbackWarning: Layer 1 발동 후 LIST success result에 critical warning prepend
//             (LLM이 이전 turn 가이드 무시하고 LIST fallback → 다음 LLM 응답 직전에 강한 경고 inject).
//   Layer 3 — finishPolicy GUARD: finalize 시 specifier 무관 응답 시 retry (별도 finishPolicy.js).
//
// 적용 위치:
//   - applyDetailNotFoundGuide: callApiInternal 결과 success=false 직후 (메시지 append + session._detailNotFoundContext 설정)
//   - applyListFallbackWarning: callApiInternal 결과 success=true 직후 (context match 시 메시지 prepend)
//   - finishPolicy GUARD: useAiChat/nodeAgent finalize gate (별도 layer)

const GUIDE_HEADER = '[DETAIL NOT FOUND]';
const LIST_WARNING_HEADER = '[CRITICAL — DETAIL FALLBACK]';

// numeric specifier: N번 / N호 / N개 (entity ID 형태). N개월/N년/N개간/N번째 등 기간/순서 표현은 자연히 제외.
const NUMERIC_SPECIFIER_RE = /\d+\s*(?:번|호|개)(?!월|년|간|째)/;

// intention category prefix 추출 (예: 'CHECK_LOCKER_DETAIL' → 'CHECK_LOCKER').
// DETAIL/LIST 같은 suffix를 잘라 LIST fallback이 같은 entity 영역인지 비교용.
export function extractIntentionPrefix(intentionKey) {
  const key = String(intentionKey || '').toUpperCase();
  if (!key) return '';
  // 마지막 _SEGMENT 제거 — CHECK_LOCKER_DETAIL → CHECK_LOCKER, CHECK_LOCKER_LIST → CHECK_LOCKER
  const m = key.match(/^(.+?)_(DETAIL|LIST|STATISTICS|TREND|RANK|BY|SUMMARY)/);
  return m ? m[1] : key;
}

export function extractSpecifier(userInput) {
  const m = String(userInput || '').match(NUMERIC_SPECIFIER_RE);
  return m ? m[0].replace(/\s+/g, '') : null;
}

export function detectDetailNotFound({ intention, userInput, errorMessage } = {}) {
  const intentionKey = String(intention || '').toUpperCase();
  if (!intentionKey.includes('DETAIL')) return null;
  if (typeof userInput !== 'string' || userInput.length === 0) return null;
  const specifier = extractSpecifier(userInput);
  if (!specifier) return null;

  const errText = typeof errorMessage === 'string' && errorMessage.length > 0 ? errorMessage : 'not found';
  return {
    pattern: 'detail_not_found',
    severity: 'critical',
    specifier,
    intentionPrefix: extractIntentionPrefix(intentionKey),
    guidance: `${GUIDE_HEADER} DETAIL 조회 실패 (${errText}). 사용자가 지정한 항목 "${specifier}"이(가) catalog에 없을 가능성.\n  (1) finalize "지정하신 항목을 찾을 수 없어요" 안내 (가장 권장).\n  (2) LIST 호출 + filter(specifier) — 결과가 1건 이상 match 시에만 응답.\n⚠ NEVER: LIST 호출 후 전체 결과를 specifier 응답으로 finalize. user를 오도함.`,
  };
}

// callApiInternal 실패 결과를 받아 DETAIL not-found 패턴 매칭 시 message에 guidance append + session에 context 기록.
// session._detailNotFoundContext = { specifier, intentionPrefix } 설정 → 후속 LIST result augmentation + finishPolicy 가드에 활용.
// idempotent — message에 이미 GUIDE_HEADER 포함 시 원본 그대로 반환.
export function applyDetailNotFoundGuide(result, ctx = {}) {
  if (!result || typeof result !== 'object') return result;
  if (result.success !== false) return result;
  const info = detectDetailNotFound({
    intention: ctx.intention ?? result.intention,
    userInput: ctx.userInput,
    errorMessage: ctx.errorMessage ?? result.message,
  });
  if (!info) return result;

  // session 객체에 context 기록 (후속 LIST result + finishPolicy 가드 트리거)
  if (ctx.session && typeof ctx.session === 'object') {
    ctx.session._detailNotFoundContext = {
      specifier: info.specifier,
      intentionPrefix: info.intentionPrefix,
    };
  }

  const msg = typeof result.message === 'string' ? result.message : '';
  if (msg.includes(GUIDE_HEADER)) return result;
  return { ...result, message: msg ? `${msg}\n\n${info.guidance}` : info.guidance };
}

// LIST(또는 STATISTICS 등 fallback) call_api success 결과에 critical warning prepend.
// 조건: session._detailNotFoundContext 존재 + 현재 intention의 prefix가 일치.
// idempotent — message에 이미 LIST_WARNING_HEADER 포함 시 원본 그대로 반환.
export function applyListFallbackWarning(result, ctx = {}) {
  if (!result || typeof result !== 'object') return result;
  if (result.success !== true) return result;
  if (!ctx.session || typeof ctx.session !== 'object') return result;
  const dctx = ctx.session._detailNotFoundContext;
  if (!dctx || !dctx.specifier || !dctx.intentionPrefix) return result;

  const currentPrefix = extractIntentionPrefix(ctx.intention ?? result.intention);
  if (currentPrefix !== dctx.intentionPrefix) return result;

  const msg = typeof result.message === 'string' ? result.message : '';
  if (msg.includes(LIST_WARNING_HEADER)) return result;

  const warning = `${LIST_WARNING_HEADER} 직전 DETAIL 호출이 "${dctx.specifier}"으로 실패했어요. 이 결과는 fallback일 뿐 — "${dctx.specifier}" 응답으로 직접 사용 금지.\n  (1) 결과 중 "${dctx.specifier}"과 매칭되는 항목이 있으면 filter로 격리 후 그것만 응답.\n  (2) 매칭 없으면 finalize "지정하신 항목(${dctx.specifier})을(를) 찾을 수 없어요" 안내.\n⚠ 전체 결과를 "${dctx.specifier} 관련 정보"로 finalize 금지 — user를 오도함.`;

  return { ...result, message: `${warning}\n\n${msg}` };
}

// finishPolicy GUARD용 — finalText가 DETAIL not-found context를 honor했는지 검사.
// honor = "찾을 수 없" / "없어요" / specifier가 finalText에 등장 + "1건" 매칭 / etc.
// returns: { detected: boolean, sample: string }
export function detectUnfulfilledSpecifier(finalText, context) {
  if (!context || !context.specifier) return { detected: false };
  const text = String(finalText || '');
  if (text.length === 0) return { detected: false };
  // honored 표현: "찾을 수 없" / "없습니다" / "없어요" / "조회되지 않" — explicit not-found 안내
  if (/찾을\s*수\s*없|없습니다|없어요|없네요|존재하지\s*않|조회되지\s*않|등록되지\s*않/.test(text)) {
    return { detected: false };
  }
  // 단일 응답 ("총 1건" / specifier만 enumerate) — filter 적용된 경우 honored
  if (/총\s*1건|단\s*1건/.test(text)) {
    return { detected: false };
  }
  return { detected: true, sample: text.slice(0, 80) };
}

export const _GUIDE_HEADER = GUIDE_HEADER;
export const _LIST_WARNING_HEADER = LIST_WARNING_HEADER;
export const _NUMERIC_SPECIFIER_RE = NUMERIC_SPECIFIER_RE;
